import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, onSnapshot, serverTimestamp, collection, query, where, writeBatch, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase'; 
import { useAuth } from '../../context/AuthContext'; 
import { useTenant } from '../../context/TenantContext'; 

import FintechHeroWidget from '../fintech/FintechHeroWidget';
import EekoPayDashboard from '../fintech/EekoPayDashboard';

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", 
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", 
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", 
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", 
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function FoundersControlPlane() {
  const { currentUser } = useAuth();
  const { tenantId } = useTenant();
  const navigate = useNavigate();

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('margins'); 
  const [isSaving, setIsSaving] = useState(false);
  const [saveToast, setSaveToast] = useState(null);
  const [showVault, setShowVault] = useState(false);

  // --- 1. GLOBAL MARGINS & REVENUE STREAMS STATE ---
  const [margins, setMargins] = useState({
    monthly_door_fee: 249.00, activation_bounty: 0.50, payroll_base_margin: 29.00, payroll_employee_margin: 5.00,
  });
  
  // --- NEW: PUBLIC RETAIL PRICING (WEBSITE DIRECT) ---
  const [publicRetail, setPublicRetail] = useState({
    basePlatform: 249.00, checkHqDoor: 30.00, userSeat: 7.00
  });

  const [dynamicMargins, setDynamicMargins] = useState([]);
  const [showMarginModal, setShowMarginModal] = useState(false);
  const [isEditingMargin, setIsEditingMargin] = useState(false);
  const [marginForm, setMarginForm] = useState({ id: '', displayName: '', cost: 0, retail: 0, isPercent: false });

  // --- 2. API PARTNER REGISTRY STATE ---
  const [integrations, setIntegrations] = useState({});
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [isEditingVendor, setIsEditingVendor] = useState(false);
  const [vendorForm, setVendorForm] = useState({ 
    id: '', displayName: '', category: '', billingType: 'PRE_FUNDED', active: true 
  });

  // --- 3. MASTER AGENT STATE & PLG INTEGRATION ---
  const [agents, setAgents] = useState([]);
  const [showAgentIntake, setShowAgentIntake] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [intakeSuccess, setIntakeSuccess] = useState(false);
  
  const [agentForm, setAgentForm] = useState({
    provisioningMethod: 'checkhq', companyName: '', dba: '', ein: '', billingStreet: '', billingCity: '',
    billingState: 'WA', billingZip: '', contactName: '', email: '', phone: '', billingContactName: '', 
    billingContactEmail: '', techContactName: '', techContactEmail: '', stripeAccountId: '', overrideCutPercent: 10,
    negotiated_door_fee: 249.00, negotiated_activation_bounty: 0.50, negotiated_payroll_base: 29.00, negotiated_payroll_emp: 5.00,
    dynamicOverrides: {},
    setupFee: 5000, 
    mrr: 499, 
    adminOverride: false 
  });

  // --- 4. DEAL DESK (1-CLICK MINT) STATE ---
  const [dealDeskLeads, setDealDeskLeads] = useState([]);
  const [activeDeal, setActiveDeal] = useState(null);
  const [showDealModal, setShowDealModal] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  
  const [dealApprovalForm, setDealApprovalForm] = useState({
    setupFee: 5000,
    mrr: 499,
    overrideCutPercent: 10,
    negotiated_door_fee: 249.00,
    negotiated_activation_bounty: 0.50,
    negotiated_payroll_base: 29.00,
    negotiated_payroll_emp: 5.00,
    dynamicOverrides: {},
    adminOverride: false,
    provisioningMethod: 'checkhq'
  });

  // --- 5. SYSTEM EXCEPTIONS (DEAD-LETTER QUEUE) STATE ---
  const [exceptions, setExceptions] = useState([]);

  const handleInitiateIntake = () => {
    const defaultOverrides = {};
    dynamicMargins.forEach(m => { defaultOverrides[m.systemKey] = m.retail; });

    setAgentForm(prev => ({
      ...prev,
      negotiated_door_fee: margins.monthly_door_fee,
      negotiated_activation_bounty: margins.activation_bounty,
      negotiated_payroll_base: margins.payroll_base_margin,
      negotiated_payroll_emp: margins.payroll_employee_margin,
      dynamicOverrides: defaultOverrides,
      setupFee: 5000, mrr: 499, adminOverride: false
    }));
    setIntakeSuccess(false);
    setShowAgentIntake(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setAgentForm(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'radio') {
      setAgentForm(prev => ({ ...prev, [name]: value }));
    } else if (name === 'ein') {
      let formatted = value.replace(/\D/g, ''); 
      if (formatted.length > 2) formatted = `${formatted.substring(0, 2)}-${formatted.substring(2, 9)}`;
      setAgentForm(prev => ({ ...prev, ein: formatted }));
    } else {
      setAgentForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDynamicOverrideChange = (systemKey, value) => {
    setAgentForm(prev => ({
      ...prev, dynamicOverrides: { ...prev.dynamicOverrides, [systemKey]: value === '' ? '' : Number(value) }
    }));
  };

  useEffect(() => {
    async function verifyFounderAccess() {
      if (!currentUser) { setIsAuthorized(false); setAuthChecking(false); return; }
      try {
        const idTokenResult = await currentUser.getIdTokenResult(true);
        const hasFounderClaim = idTokenResult.claims?.SYS_FOUNDER === true;
        const isMasterAccount = currentUser.email === 'matt@reinhardtsaastech.com';
        setIsAuthorized(hasFounderClaim || isMasterAccount);
      } catch (err) {
        setIsAuthorized(false);
      } finally {
        setAuthChecking(false);
      }
    }
    verifyFounderAccess();
  }, [currentUser]);

  // STREAM: The Shared Brain (Global Settings)
  useEffect(() => {
    if (!isAuthorized) return;
    const unsub = onSnapshot(doc(db, 'system_config', 'global_settings'), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.pricing_margins) setMargins(data.pricing_margins);
        if (data.public_retail_margins) setPublicRetail(data.public_retail_margins);
        if (data.dynamic_margins) setDynamicMargins(data.dynamic_margins);
        if (data.master_agents) setAgents(data.master_agents);
        if (data.active_integrations) setIntegrations(data.active_integrations);
      }
    });
    return () => unsub();
  }, [isAuthorized]);

  // STREAM: Deal Desk Leads
  useEffect(() => {
    if (!isAuthorized) return;
    const leadsRef = collection(db, 'clients', 'reinhardt_saas', 'crm_leads');
    const q = query(leadsRef, where('status', '==', 'Deal Desk Review'));
    
    const unsubLeads = onSnapshot(q, (snapshot) => {
      const leads = [];
      snapshot.forEach(doc => leads.push({ id: doc.id, ...doc.data() }));
      setDealDeskLeads(leads);
    });
    return () => unsubLeads();
  }, [isAuthorized]);

  // STREAM: System Exceptions (Dead-Letter Queue) Scoped by Tenant
  useEffect(() => {
    if (!isAuthorized || !tenantId) return;
    const exceptionsRef = collection(db, 'clients', tenantId, 'system_exceptions');
    const q = query(exceptionsRef, where('status', '==', 'UNRESOLVED'));
    
    const unsub = onSnapshot(q, (snapshot) => {
      const exps = [];
      snapshot.forEach(doc => exps.push({ id: doc.id, ...doc.data() }));
      // Sort descending locally to show newest errors first
      exps.sort((a, b) => (b.timestamp?.toMillis() || 0) - (a.timestamp?.toMillis() || 0));
      setExceptions(exps);
    });
    return () => unsub();
  }, [isAuthorized, tenantId]);

  // --- EXCEPTION RESOLUTION LOGIC ---
  const dismissException = async (exceptionId) => {
    try {
      await updateDoc(doc(db, `clients/${tenantId}/system_exceptions`, exceptionId), {
        status: 'DISMISSED',
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      alert("Failed to dismiss exception: " + err.message);
    }
  };

  // --- DUAL-WRITE SYNCHRONIZATION ENGINE ---
  const pushGlobalConfig = async (overrideAgents, overrideMargins, overrideIntegrations) => {
    setIsSaving(true);
    try {
      const batch = writeBatch(db);

      // 1. Internal Audit Ledger (Secured)
      const globalRef = doc(db, 'system_config', 'global_settings');
      batch.set(globalRef, {
        pricing_margins: margins,
        public_retail_margins: {
          basePlatform: Number(publicRetail.basePlatform),
          checkHqDoor: Number(publicRetail.checkHqDoor),
          userSeat: Number(publicRetail.userSeat)
        },
        dynamic_margins: overrideMargins || dynamicMargins,
        active_integrations: overrideIntegrations || integrations,
        master_agents: overrideAgents || agents,
        lastUpdatedBy: currentUser?.email || 'SYS_FOUNDER',
        updatedAt: serverTimestamp()
      }, { merge: true });

      // 2. Public Bridge Document (Website Readable)
      const publicRef = doc(db, 'platform_configs', 'public_pricing');
      batch.set(publicRef, {
        basePlatform: Number(publicRetail.basePlatform),
        checkHqDoor: Number(publicRetail.checkHqDoor),
        userSeat: Number(publicRetail.userSeat),
        updatedAt: serverTimestamp()
      });

      await batch.commit();

      setSaveToast('Network & Public Bridge Synchronized Successfully');
      setTimeout(() => setSaveToast(null), 3000);
    } catch (err) {
      alert(`Synchronization Failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarginUpdate = (key, value) => {
    setMargins({ ...margins, [key]: value === '' ? '' : Number(value) });
  };
  
  const handlePublicRetailUpdate = (key, value) => {
    setPublicRetail({ ...publicRetail, [key]: value === '' ? '' : Number(value) });
  };

  // --- REVENUE STREAM LOGIC ---
  const openNewMarginModal = () => {
    setMarginForm({ id: '', displayName: '', cost: 0, retail: 0, isPercent: false });
    setIsEditingMargin(false);
    setShowMarginModal(true);
  };

  const openEditMarginModal = (margin) => {
    setMarginForm(margin);
    setIsEditingMargin(true);
    setShowMarginModal(true);
  };

  const handleMarginSubmit = async (e) => {
    e.preventDefault();
    if (!marginForm.displayName.trim()) return;

    const sysKey = marginForm.displayName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    const newMargin = {
      id: isEditingMargin ? marginForm.id : Date.now().toString(),
      systemKey: isEditingMargin ? marginForm.systemKey : sysKey,
      displayName: marginForm.displayName.trim(),
      cost: Number(marginForm.cost),
      retail: Number(marginForm.retail),
      isPercent: marginForm.isPercent
    };

    let updatedMargins;
    if (isEditingMargin) {
      updatedMargins = dynamicMargins.map(m => m.id === newMargin.id ? newMargin : m);
    } else {
      updatedMargins = [...dynamicMargins, newMargin];
    }
    
    setDynamicMargins(updatedMargins);
    setShowMarginModal(false);
    await pushGlobalConfig(agents, updatedMargins, integrations);
  };

  const handleStrictMarginDelete = async () => {
    const confirmation = window.prompt(`CRITICAL ACTION: Deleting this stops revenue collection for this service.\n\nTo confirm, type exactly: ${marginForm.displayName}`);
    if (confirmation === marginForm.displayName) {
      const updatedMargins = dynamicMargins.filter(m => m.id !== marginForm.id);
      setDynamicMargins(updatedMargins);
      setShowMarginModal(false);
      await pushGlobalConfig(agents, updatedMargins, integrations);
    } else if (confirmation !== null) {
      alert("Validation failed. Deletion aborted.");
    }
  };

  // --- API PARTNER REGISTRY LOGIC ---
  const openNewVendorModal = () => {
    setVendorForm({ id: '', displayName: '', category: '', billingType: 'PRE_FUNDED', active: true });
    setIsEditingVendor(false);
    setShowVendorModal(true);
  };

  const openEditVendorModal = (sysKey, vendorData) => {
    setVendorForm({
      id: sysKey,
      displayName: vendorData.name || sysKey,
      category: vendorData.category || '',
      billingType: vendorData.billingType || 'PRE_FUNDED',
      active: vendorData.active
    });
    setIsEditingVendor(true);
    setShowVendorModal(true);
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!vendorForm.displayName.trim()) return;

    const sysKey = isEditingVendor ? vendorForm.id : vendorForm.displayName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    const updatedIntegrations = {
      ...integrations,
      [sysKey]: {
        name: vendorForm.displayName.trim(),
        category: vendorForm.category || 'API Integration',
        billingType: vendorForm.billingType,
        active: isEditingVendor ? vendorForm.active : true
      }
    };

    setIntegrations(updatedIntegrations);
    setShowVendorModal(false);
    await pushGlobalConfig(agents, dynamicMargins, updatedIntegrations);
  };

  const toggleVendorGateway = async () => {
    setVendorForm({ ...vendorForm, active: !vendorForm.active });
  };

  const executeInstantKillSwitch = async (sysKey, currentActiveState) => {
    const updatedIntegrations = {
      ...integrations,
      [sysKey]: { ...integrations[sysKey], active: !currentActiveState }
    };
    setIntegrations(updatedIntegrations);
    await pushGlobalConfig(agents, dynamicMargins, updatedIntegrations);
  };

  const handleStrictVendorDelete = async () => {
    const confirmation = window.prompt(`CRITICAL ACTION: This permanently severs this API connection.\n\nTo confirm, type exactly: ${vendorForm.displayName}`);
    if (confirmation === vendorForm.displayName) {
      const updatedIntegrations = { ...integrations };
      delete updatedIntegrations[vendorForm.id];
      
      setIntegrations(updatedIntegrations);
      setShowVendorModal(false);
      await pushGlobalConfig(agents, dynamicMargins, updatedIntegrations);
    } else if (confirmation !== null) {
      alert("Validation failed. Deletion aborted.");
    }
  };

  const removeAgent = (agentToRemove) => {
    if (window.confirm(`Permanently delete ${agentToRemove.name}?`)) {
      const updatedAgents = agents.filter(a => a.id !== agentToRemove.id);
      setAgents(updatedAgents);
      pushGlobalConfig(updatedAgents, dynamicMargins, integrations);
    }
  };

  // --- THE MASTER AGENT PROVISIONING ENGINE (Manual PLG Route) ---
  const submitMasterAgent = async (e) => {
    e.preventDefault();
    if (agentForm.ein.length !== 10) return alert("Please ensure the corporate EIN is exactly 9 digits.");
    setIsSubmitting(true);
    try {
      const provisionMasterAgentFn = httpsCallable(functions, 'provisionMasterAgent');
      const result = await provisionMasterAgentFn({
        companyName: agentForm.companyName, 
        dba: agentForm.dba, 
        ein: agentForm.ein,
        billingStreet: agentForm.billingStreet, 
        billingCity: agentForm.billingCity,
        billingState: agentForm.billingState, 
        billingZip: agentForm.billingZip,
        contactName: agentForm.contactName, 
        email: agentForm.email, 
        phone: agentForm.phone,
        billingContactName: agentForm.billingContactName, 
        billingContactEmail: agentForm.billingContactEmail,
        techContactName: agentForm.techContactName, 
        techContactEmail: agentForm.techContactEmail,
        provisioningMethod: agentForm.provisioningMethod, 
        stripeAccountId: agentForm.stripeAccountId,
        overrideCutPercent: Number(agentForm.overrideCutPercent),
        homeStoreId: 'store_corporate_reinhardt', 
        systemRole: 'MASTER_AGENT', 
        negotiatedRates: {
          door_fee: Number(agentForm.negotiated_door_fee), 
          activation_bounty: Number(agentForm.negotiated_activation_bounty),
          payroll_base: Number(agentForm.negotiated_payroll_base), 
          payroll_emp: Number(agentForm.negotiated_payroll_emp),
          ...agentForm.dynamicOverrides
        }
      });

      if (result.data.success) {
        const newMasterAgentId = result.data.masterAgentId;
        const newAgent = {
          id: newMasterAgentId, 
          name: agentForm.companyName, 
          dba: agentForm.dba, 
          ein: agentForm.ein,
          homeStoreId: 'store_corporate_reinhardt', 
          systemRole: 'MASTER_AGENT',
          address: `${agentForm.billingStreet}, ${agentForm.billingCity}, ${agentForm.billingState} ${agentForm.billingZip}`,
          contacts: {
            executive: { name: agentForm.contactName, email: agentForm.email, phone: agentForm.phone },
            billing: { name: agentForm.billingContactName, email: agentForm.billingContactEmail },
            technical: { name: agentForm.techContactName, email: agentForm.techContactEmail }
          },
          provisioningMethod: agentForm.provisioningMethod, 
          stripeAccountId: agentForm.stripeAccountId || 'Pending Stripe Connect',
          overrideCutPercent: Number(agentForm.overrideCutPercent), 
          status: 'pre_launch', 
          onboardingComplete: false,
          negotiatedRates: {
            door_fee: Number(agentForm.negotiated_door_fee), 
            activation_bounty: Number(agentForm.negotiated_activation_bounty),
            payroll_base: Number(agentForm.negotiated_payroll_base), 
            payroll_emp: Number(agentForm.negotiated_payroll_emp),
            ...agentForm.dynamicOverrides
          },
          createdAt: new Date().toISOString()
        };
        const updatedAgents = [...agents, newAgent];
        setAgents(updatedAgents);
        pushGlobalConfig(updatedAgents, dynamicMargins, integrations);

        // Transition completely to PLG Tollbooth success UI
        setIntakeSuccess(true);
      }
    } catch (err) { 
      alert(`Provisioning Error: ${err.message}`); 
    } finally { 
      setIsSubmitting(false); 
    }
  };

  // --- DEAL DESK 1-CLICK MINTING ENGINE (Deal Desk PLG Route) ---
  const handleOpenDealApproval = (deal) => {
    setActiveDeal(deal);
    
    const defaultOverrides = {};
    dynamicMargins.forEach(m => { defaultOverrides[m.systemKey] = m.retail; });

    setDealApprovalForm({
      setupFee: deal.calculatedOneTime || 5000,
      mrr: deal.mrr_value || 499,
      overrideCutPercent: 10,
      negotiated_door_fee: margins.monthly_door_fee || 249.00,
      negotiated_activation_bounty: margins.activation_bounty || 0.50,
      negotiated_payroll_base: margins.payroll_base_margin || 29.00,
      negotiated_payroll_emp: margins.payroll_employee_margin || 5.00,
      dynamicOverrides: defaultOverrides,
      adminOverride: false,
      provisioningMethod: 'checkhq'
    });
    
    setApprovalSuccess(false);
    setShowDealModal(true);
  };

  const handleApprovalDynamicOverrideChange = (systemKey, value) => {
    setDealApprovalForm(prev => ({
      ...prev, dynamicOverrides: { ...prev.dynamicOverrides, [systemKey]: value === '' ? '' : Number(value) }
    }));
  };

  const submitDealApproval = async (e) => {
    e.preventDefault();
    setIsApproving(true);
    try {
      const provisionMasterAgentFn = httpsCallable(functions, 'provisionMasterAgent');
      
      let street = '', city = '', state = 'WA', zip = '';
      if (activeDeal.billingAddress) {
         const parts = activeDeal.billingAddress.split(',').map(p => p.trim());
         street = parts[0] || '';
         city = parts[1] || '';
         const sz = (parts[2] || '').split(' ').filter(Boolean);
         state = sz[0] || 'WA';
         zip = sz[1] || '';
      }

      const result = await provisionMasterAgentFn({
        companyName: activeDeal.company_name || activeDeal.company,
        dba: activeDeal.dba || '',
        ein: activeDeal.ein,
        billingStreet: street,
        billingCity: city,
        billingState: state,
        billingZip: zip,
        contactName: activeDeal.contact_name || activeDeal.contactName,
        email: activeDeal.contactEmail || activeDeal.email,
        phone: activeDeal.contactPhone || activeDeal.phone,
        billingContactName: activeDeal.billingContactName || activeDeal.contact_name,
        billingContactEmail: activeDeal.billingEmail || activeDeal.contactEmail,
        techContactName: activeDeal.techContactName || activeDeal.contact_name,
        techContactEmail: activeDeal.techEmail || activeDeal.contactEmail,
        provisioningMethod: dealApprovalForm.provisioningMethod,
        stripeAccountId: activeDeal.stripeAccountId || '',
        overrideCutPercent: Number(dealApprovalForm.overrideCutPercent),
        homeStoreId: 'store_corporate_reinhardt', 
        systemRole: 'MASTER_AGENT', 
        negotiatedRates: {
          door_fee: Number(dealApprovalForm.negotiated_door_fee), 
          activation_bounty: Number(dealApprovalForm.negotiated_activation_bounty),
          payroll_base: Number(dealApprovalForm.negotiated_payroll_base), 
          payroll_emp: Number(dealApprovalForm.negotiated_payroll_emp),
          ...dealApprovalForm.dynamicOverrides
        }
      });

      if (result.data.success) {
         // Update lead status so it drops out of the Deal Desk Queue automatically
         await setDoc(doc(db, 'clients', 'reinhardt_saas', 'crm_leads', activeDeal.id), {
           status: 'Active Tenant',
           stage: 'closedWon',
           homeStoreId: 'store_corporate_reinhardt',
           systemRole: 'MASTER_AGENT',
           paymentStatus: dealApprovalForm.adminOverride ? 'paid' : 'pending_ftl',
           updatedAt: serverTimestamp()
         }, { merge: true });

         setApprovalSuccess(true);
      }
    } catch (err) {
      alert(`Approval Error: ${err.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  if (authChecking) return <div className="min-h-[80vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!isAuthorized) return <div className="min-h-[80vh] bg-white flex flex-col items-center justify-center p-6 text-center"><h1 className="text-2xl font-black text-slate-900 uppercase tracking-widest mb-2">Protocol 403: Access Denied</h1></div>;

  return (
    <div className="min-h-[85vh] bg-slate-50 text-slate-900 font-sans pb-12 shadow-sm relative rounded-md overflow-hidden border border-slate-200">
      
      {saveToast && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 bg-emerald-50 border border-emerald-200 text-emerald-700 px-6 py-3 shadow-lg animate-in slide-in-from-top-4 flex items-center gap-3 rounded">
          <i className="fa-solid fa-satellite-dish animate-pulse"></i><span className="text-xs font-black uppercase tracking-widest">{saveToast}</span>
        </div>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded flex items-center justify-center shadow-sm">
              <i className="fa-solid fa-network-wired text-lg"></i>
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest leading-tight">Master Control Plane</h1>
              <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest flex items-center gap-2 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Network Linked & Secured
              </p>
            </div>
          </div>
          <button 
            onClick={() => pushGlobalConfig()} 
            disabled={isSaving}
            className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2 rounded-sm"
          >
            {isSaving ? <><i className="fa-solid fa-spinner fa-spin"></i> Syncing...</> : <><i className="fa-solid fa-cloud-arrow-up"></i> Deploy Global Rules</>}
          </button>
        </div>

        <div className="px-6 flex gap-6 pt-2 border-t border-slate-100 overflow-x-auto">
          {[
            { id: 'margins', label: 'Global Margins & Yield', icon: 'fa-money-bill-trend-up' },
            { id: 'integrations', label: 'API Partner Registry', icon: 'fa-handshake' },
            { id: 'agents', label: 'Master Agent Routing', icon: 'fa-users-gear' },
            { id: 'exceptions', label: 'System Exceptions', icon: 'fa-triangle-exclamation' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 pt-2 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors border-b-2 whitespace-nowrap ${
                activeTab === tab.id ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <i className={`fa-solid ${tab.icon}`}></i> {tab.label}
              {tab.id === 'exceptions' && exceptions.length > 0 && (
                <span className="bg-rose-500 text-white text-[8px] px-1.5 py-0.5 rounded-sm ml-1">{exceptions.length}</span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="px-6 py-8">
        <FintechHeroWidget onOpenVault={() => setShowVault(true)} />

        {/* --- GLOBAL DEAL DESK QUEUE WIDGET --- */}
        <div className="mb-10 bg-white border border-slate-300 shadow-sm rounded-sm overflow-hidden">
          <div className="bg-slate-900 px-6 py-4 flex justify-between items-center">
            <div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
                <i className="fa-solid fa-file-signature text-emerald-400"></i> Deal Desk Approval Queue
              </h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Pending Master Agent Executions</p>
            </div>
            <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-3 py-1 text-[10px] font-black rounded-sm shadow-inner uppercase tracking-widest">
              {dealDeskLeads.length} Actionable
            </span>
          </div>
          
          <div className="p-0">
            {dealDeskLeads.length === 0 ? (
              <div className="p-10 text-center bg-slate-50">
                <i className="fa-solid fa-inbox text-3xl text-slate-300 mb-3"></i>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Queue is clear.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {dealDeskLeads.map(deal => (
                  <div key={deal.id} className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-100 border border-slate-200 text-slate-500 flex items-center justify-center font-black text-lg rounded-sm shadow-sm">
                        {(deal.company_name || deal.company || 'U')[0].toUpperCase()}
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-slate-900">{deal.company_name || deal.company}</h4>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">
                          EIN: {deal.ein || 'Missing'} • Rep: <span className="text-indigo-600">{deal.rep_name || 'Unassigned'}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right hidden md:block">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Proposed Deal Value</p>
                        <p className="text-sm font-black text-emerald-600">
                          ${(deal.calculatedOneTime || 5000).toLocaleString()} <span className="text-slate-400 text-[10px]">Setup</span> / ${(deal.mrr_value || 499).toLocaleString()} <span className="text-slate-400 text-[10px]">MRR</span>
                        </p>
                      </div>
                      <button 
                        onClick={() => handleOpenDealApproval(deal)}
                        className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 text-[10px] font-bold py-2.5 px-6 uppercase tracking-widest shadow-sm transition-colors rounded-sm flex items-center gap-2"
                      >
                        Review & Mint <i className="fa-solid fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* --- TAB 1: GLOBAL MARGINS & DYNAMIC SPREADS --- */}
        {activeTab === 'margins' && (
          <div className="animate-in fade-in duration-300">
            <div className="mb-6 flex justify-between items-end">
              <div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest">Global SaaS Pricing & Yield Engine</h2>
                <p className="text-xs text-slate-500 mt-1">Configure foundational subscriptions and deploy dynamic micro-transaction spreads to all Apex OS terminals.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white border border-slate-200 p-6 shadow-sm rounded-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5">Apex Enterprise Base (Fixed)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Monthly Door Fee (MRR)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" value={margins.monthly_door_fee} onChange={(e) => handleMarginUpdate('monthly_door_fee', e.target.value)} className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-7 pr-3 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Telecom Activation Bounty</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" step="0.50" value={margins.activation_bounty} onChange={(e) => handleMarginUpdate('activation_bounty', e.target.value)} className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-7 pr-3 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-6 shadow-sm rounded-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5">CheckHQ Payroll Integration (Fixed)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Monthly Location Base Fee</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" value={margins.payroll_base_margin} onChange={(e) => handleMarginUpdate('payroll_base_margin', e.target.value)} className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-7 pr-3 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Per-Employee Processing Fee</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" value={margins.payroll_employee_margin} onChange={(e) => handleMarginUpdate('payroll_employee_margin', e.target.value)} className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-7 pr-3 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm" />
                    </div>
                  </div>
                </div>
              </div>

              {/* NEW: PUBLIC RETAIL PRICING DUAL-WRITE MODULE */}
              <div className="bg-white border border-slate-200 p-6 shadow-sm rounded-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5">Public Retail Pricing (Website Direct)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Base Platform Fee</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input type="number" value={publicRetail.basePlatform} onChange={(e) => handlePublicRetailUpdate('basePlatform', e.target.value)} className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-7 pr-3 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">CheckHQ Base</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                        <input type="number" value={publicRetail.checkHqDoor} onChange={(e) => handlePublicRetailUpdate('checkHqDoor', e.target.value)} className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-7 pr-3 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">User Seat Fee</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                        <input type="number" value={publicRetail.userSeat} onChange={(e) => handlePublicRetailUpdate('userSeat', e.target.value)} className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-7 pr-3 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* DYNAMIC SPREAD BUILDER (CARD UX) */}
            <div className="mb-4 flex justify-between items-end">
              <div>
                <h3 className="text-lg font-black text-slate-900 uppercase tracking-widest">Revenue Streams & API Spreads</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Configure micro-transaction margins. Saves auto-deploy instantly to the network.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              
              {/* Add New Margin Card */}
              <div 
                onClick={openNewMarginModal}
                className="bg-slate-50 border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50 rounded-sm p-6 flex flex-col items-center justify-center min-h-[160px] cursor-pointer transition-all group shadow-sm"
              >
                <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-emerald-500 group-hover:border-emerald-200 mb-3 transition-colors shadow-sm">
                  <i className="fa-solid fa-plus text-xl"></i>
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 group-hover:text-emerald-700">Add Revenue Stream</p>
              </div>

              {/* Render Active Margins */}
              {dynamicMargins.map((margin) => {
                const profit = Number(margin.retail) - Number(margin.cost);
                return (
                  <div 
                    key={margin.id} 
                    onClick={() => openEditMarginModal(margin)}
                    className="bg-white border border-slate-200 rounded-sm shadow-sm flex flex-col relative overflow-hidden transition-all hover:shadow-md hover:border-slate-300 cursor-pointer group"
                  >
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-4">
                        <div className="w-10 h-10 rounded bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center shrink-0">
                           <i className="fa-solid fa-money-bill-transfer"></i>
                        </div>
                        <span className="text-[8px] font-black bg-slate-100 text-slate-500 px-2 py-0.5 rounded border border-slate-200 uppercase tracking-widest">
                          {margin.isPercent ? 'MARKUP (%)' : 'FLAT ($)'}
                        </span>
                      </div>
                      
                      <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight truncate w-full">{margin.displayName}</h3>
                      
                      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Wholesale Cost</p>
                          <p className="text-xs font-black text-rose-500">{margin.isPercent ? `${margin.cost}%` : `$${Number(margin.cost).toFixed(3)}`}</p>
                        </div>
                        <div>
                          <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Retail Charge</p>
                          <p className="text-xs font-black text-slate-800">{margin.isPercent ? `${margin.retail}%` : `$${Number(margin.retail).toFixed(3)}`}</p>
                        </div>
                        <div className="col-span-2 mt-2 bg-emerald-50 p-2 rounded border border-emerald-100 flex justify-between items-center">
                           <span className="text-[9px] font-black text-emerald-700 uppercase tracking-widest">Net Yield</span>
                           <span className="text-sm font-black text-emerald-600">{margin.isPercent ? `${profit.toFixed(1)}%` : `$${profit.toFixed(3)}`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* --- TAB 2: API PARTNER REGISTRY --- */}
        {activeTab === 'integrations' && (
          <div className="animate-in fade-in duration-300">
            <div className="flex justify-between items-end mb-8">
              <div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest">API Partner Registry</h2>
                <p className="text-xs text-slate-500 mt-1">Manage approved ecosystem partners and global API kill-switches.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              
              {/* Add New Partner Card */}
              <div 
                onClick={openNewVendorModal}
                className="bg-slate-50 border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50 rounded-sm p-6 flex flex-col items-center justify-center min-h-[220px] cursor-pointer transition-all group shadow-sm"
              >
                <div className="w-14 h-14 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-emerald-500 group-hover:border-emerald-200 mb-4 transition-colors shadow-sm">
                  <i className="fa-solid fa-plus text-2xl"></i>
                </div>
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 group-hover:text-emerald-700">Add API Provider</p>
              </div>

              {/* Active Vendor Cards */}
              {Object.entries(integrations).map(([sysKey, vendor]) => {
                const isActive = vendor.active !== false;
                return (
                  <div 
                    key={sysKey} 
                    className="bg-white border border-slate-200 rounded-sm shadow-sm flex flex-col relative overflow-hidden transition-all hover:shadow-md hover:border-slate-300 group"
                  >
                    <div className={`h-1.5 w-full ${isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                    
                    <div 
                      onClick={() => openEditVendorModal(sysKey, vendor)}
                      className="p-6 flex-1 flex flex-col items-center text-center cursor-pointer"
                    >
                      <div className="w-16 h-16 rounded bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shrink-0 mb-4 shadow-sm group-hover:scale-105 transition-transform">
                        <span className="text-slate-400 font-black text-2xl">{(vendor.name || sysKey).charAt(0).toUpperCase()}</span>
                      </div>
                      
                      <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight truncate w-full">{vendor.name || sysKey}</h3>
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-1">{vendor.category || 'Integration'}</p>
                    </div>

                    <div className="px-6 pb-6 pt-2 w-full flex items-center justify-between border-t border-slate-100 bg-slate-50">
                      <span className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-sm border ${isActive ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-rose-50 text-rose-600 border-rose-200'}`}>
                        {isActive ? 'Gateway Open' : 'Traffic Suspended'}
                      </span>
                      
                      <button 
                        onClick={(e) => { e.stopPropagation(); executeInstantKillSwitch(sysKey, isActive); }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* --- TAB 3: MASTER AGENTS --- */}
        {activeTab === 'agents' && (
          <div className="animate-in fade-in duration-300">
            <div className="flex justify-between items-end mb-8">
              <div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest">Master Agent Intake</h2>
                <p className="text-xs text-slate-500 mt-1">Register Enterprise Master Agents and configure their network billing structure.</p>
              </div>
              <button onClick={handleInitiateIntake} className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors flex items-center gap-2 rounded-sm shadow-sm">
                <i className="fa-solid fa-file-contract"></i> Initiate Master Agreement
              </button>
            </div>

            {agents.length === 0 ? (
              <div className="py-16 text-center border-2 border-dashed border-slate-200 bg-slate-50 rounded-md">
                <div className="w-16 h-16 bg-white border border-slate-200 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-network-wired text-2xl"></i>
                </div>
                <h3 className="text-sm font-bold text-slate-600 uppercase tracking-widest">No Master Agents</h3>
                <p className="text-xs text-slate-400 mt-2 max-w-sm mx-auto">Your master agent network is currently empty.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {agents.map((agent, index) => (
                  <div key={agent.id || index} className="bg-white border border-slate-200 p-6 flex flex-col justify-between shadow-sm rounded-sm relative group overflow-hidden">
                    <button onClick={() => removeAgent(agent)} className="absolute top-4 right-4 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-3 py-1.5 rounded-sm transition-colors flex items-center gap-2 text-[10px] font-black uppercase tracking-widest z-10 shadow-sm">
                      <i className="fa-solid fa-trash"></i> Delete
                    </button>
                    <div>
                      <div className="flex justify-between items-start mb-4 pr-24">
                        <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700 font-black text-lg rounded-sm">
                          {(agent.name || agent.companyName || 'U').charAt(0).toUpperCase()}
                        </div>
                        <span className={`text-[9px] font-black border px-2 py-0.5 uppercase tracking-widest rounded-sm ${agent.status === 'Provisioning...' ? 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                          {agent.status || 'Active'}
                        </span>
                      </div>
                      <h3 className="text-lg font-black text-slate-900 mb-1 truncate pr-20">{agent.name || agent.companyName || 'Unknown Entity'}</h3>
                      <p className="text-xs font-medium text-slate-500 flex items-center gap-2 mb-6">
                        <i className="fa-regular fa-envelope text-slate-400"></i> {(agent.contacts && agent.contacts.executive && agent.contacts.executive.email) || agent.contactEmail || agent.email || 'No email'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- TAB 4: SYSTEM EXCEPTIONS (DEAD-LETTER QUEUE) --- */}
        {activeTab === 'exceptions' && (
          <div className="animate-in fade-in duration-300">
            <div className="mb-6 flex justify-between items-end">
              <div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest">Exception Management</h2>
                <p className="text-xs text-slate-500 mt-1">Dead-Letter Queue for failed webhooks, missing ledger mappings, and orphaned tasks.</p>
              </div>
              {exceptions.length > 0 && (
                <span className="bg-rose-50 text-rose-600 border border-rose-200 px-3 py-1 text-[10px] font-black rounded-sm shadow-inner uppercase tracking-widest">
                  {exceptions.length} Unresolved {exceptions.length === 1 ? 'Error' : 'Errors'}
                </span>
              )}
            </div>

            {exceptions.length === 0 ? (
              <div className="py-16 text-center border-2 border-dashed border-slate-200 bg-slate-50 rounded-md">
                <div className="w-16 h-16 bg-white border border-slate-200 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-shield-check text-2xl"></i>
                </div>
                <h3 className="text-sm font-bold text-slate-600 uppercase tracking-widest">System Healthy</h3>
                <p className="text-xs text-slate-400 mt-2 max-w-sm mx-auto">No unhandled exceptions or dead-letter payloads found in the queue.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {exceptions.map(ex => (
                  <div key={ex.id} className="bg-white border border-rose-200 shadow-sm rounded-sm overflow-hidden flex flex-col">
                    <div className="bg-rose-50/50 border-b border-rose-100 p-4 flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 text-rose-500">
                          <i className="fa-solid fa-triangle-exclamation"></i>
                        </div>
                        <div>
                          <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">{ex.source || 'UNKNOWN_SOURCE'}</h4>
                          <p className="text-[10px] font-bold text-rose-600 mt-1">{ex.error || 'System failed to process the payload.'}</p>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-2">
                            {ex.timestamp?.toDate ? ex.timestamp.toDate().toLocaleString() : 'Just Now'} • ID: {ex.id}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => dismissException(ex.id)} 
                          className="bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 text-slate-600 hover:text-rose-700 text-[9px] font-black px-4 py-2 uppercase tracking-widest transition-colors rounded-sm shadow-sm"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-900 overflow-x-auto">
                      <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-2">Failed Payload (JSON)</p>
                      <pre className="text-[10px] font-mono text-emerald-400 leading-relaxed">
                        {JSON.stringify(ex.payload, null, 2)}
                      </pre>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </main>

      {/* --- ADD/EDIT REVENUE STREAM MODAL --- */}
      {showMarginModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white border border-slate-200 max-w-sm w-full rounded-sm shadow-2xl overflow-hidden flex flex-col">
            <div className="bg-slate-900 p-5 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-sm font-black text-white uppercase tracking-tight">
                  {isEditingMargin ? 'Manage Revenue Stream' : 'Add Revenue Stream'}
                </h3>
              </div>
              <button onClick={() => setShowMarginModal(false)} className="text-slate-400 hover:text-white transition-colors">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form id="margin-form" onSubmit={handleMarginSubmit} className="space-y-5">
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Service Display Name <span className="text-red-500">*</span></label>
                  <input 
                    type="text" required placeholder="e.g., Background Check" 
                    value={marginForm.displayName} onChange={(e) => setMarginForm({...marginForm, displayName: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-300 text-slate-900 px-3 py-2.5 text-sm font-bold focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner"
                  />
                </div>

                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Spread Type</label>
                  <select 
                    value={marginForm.isPercent ? 'percent' : 'flat'} 
                    onChange={(e) => setMarginForm({...marginForm, isPercent: e.target.value === 'percent'})}
                    className="w-full bg-slate-50 border border-slate-300 text-slate-900 px-3 py-2.5 text-xs font-bold uppercase tracking-widest focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner cursor-pointer"
                  >
                    <option value="flat">Flat Dollar Amount ($)</option>
                    <option value="percent">Percentage Markup (%)</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Wholesale Cost</label>
                    <div className="relative">
                      {!marginForm.isPercent && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>}
                      <input 
                        type="number" step="0.001" required placeholder="0.00" 
                        value={marginForm.cost} onChange={(e) => setMarginForm({...marginForm, cost: e.target.value})}
                        className={`w-full bg-rose-50 border border-rose-200 text-rose-700 py-2 text-sm font-black focus:outline-none focus:border-rose-400 rounded-sm shadow-inner ${marginForm.isPercent ? 'px-3' : 'pl-7 pr-3'}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Retail Charge</label>
                    <div className="relative">
                      {!marginForm.isPercent && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>}
                      <input 
                        type="number" step="0.001" required placeholder="0.00" 
                        value={marginForm.retail} onChange={(e) => setMarginForm({...marginForm, retail: e.target.value})}
                        className={`w-full bg-slate-50 border border-slate-300 text-slate-900 py-2 text-sm font-black focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner ${marginForm.isPercent ? 'px-3' : 'pl-7 pr-3'}`}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="bg-slate-50 p-5 border-t border-slate-200 flex justify-between items-center shrink-0">
              {isEditingMargin ? (
                <button type="button" onClick={handleStrictMarginDelete} className="text-[10px] font-black uppercase tracking-widest text-rose-600 hover:text-rose-800 transition-colors">
                  <i className="fa-solid fa-trash-can mr-1"></i> Delete
                </button>
              ) : <div></div>}
              
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowMarginModal(false)} className="bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-[10px] font-black uppercase tracking-widest px-6 py-2.5 rounded-sm transition-colors shadow-sm">Cancel</button>
                <button type="submit" form="margin-form" className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest px-6 py-2.5 rounded-sm shadow-sm transition-colors">
                  {isEditingMargin ? 'Save Yield' : 'Add Yield'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- ADD/EDIT VENDOR MODAL --- */}
      {showVendorModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white border border-slate-200 max-w-sm w-full rounded-sm shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-slate-900 p-5 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-sm font-black text-white uppercase tracking-tight">
                  {isEditingVendor ? 'Manage API Partner' : 'Add API Partner'}
                </h3>
              </div>
              <button onClick={() => setShowVendorModal(false)} className="text-slate-400 hover:text-white transition-colors">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form id="vendor-form" onSubmit={handleVendorSubmit} className="space-y-5">
                
                {isEditingVendor && (
                  <div className="flex justify-between items-center bg-slate-50 p-4 border border-slate-200 rounded-sm mb-2">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-700">Network Gateway</p>
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Toggle live routing.</p>
                    </div>
                    <button 
                      type="button"
                      onClick={toggleVendorGateway}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${vendorForm.active ? 'bg-emerald-500' : 'bg-rose-500'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${vendorForm.active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Corporate / Display Name <span className="text-red-500">*</span></label>
                  <input 
                    type="text" required placeholder="e.g., Boss Revolution" 
                    value={vendorForm.displayName} onChange={(e) => setVendorForm({...vendorForm, displayName: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-300 text-slate-900 px-3 py-2.5 text-sm font-bold focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner"
                  />
                </div>
                
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Integration Category</label>
                  <input 
                    type="text" placeholder="e.g., Bill Pay, Telecom, FinTech" 
                    value={vendorForm.category} onChange={(e) => setVendorForm({...vendorForm, category: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-300 text-slate-900 px-3 py-2.5 text-sm font-bold focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner"
                  />
                </div>

                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Financial Architecture</label>
                  <select 
                    value={vendorForm.billingType} 
                    onChange={(e) => setVendorForm({...vendorForm, billingType: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-300 text-slate-900 px-3 py-2.5 text-xs font-bold uppercase tracking-widest focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner cursor-pointer"
                  >
                    <option value="PRE_FUNDED">Pre-Funded (Requires Master Escrow Float)</option>
                    <option value="REV_SHARE">Revenue Share (Direct Payouts)</option>
                  </select>
                </div>
              </form>
            </div>

            <div className="bg-slate-50 p-5 border-t border-slate-200 flex justify-between items-center shrink-0">
              {isEditingVendor ? (
                <button type="button" onClick={handleStrictVendorDelete} className="text-[10px] font-black uppercase tracking-widest text-rose-600 hover:text-rose-800 transition-colors">
                  <i className="fa-solid fa-trash-can mr-1"></i> Sever
                </button>
              ) : <div></div>}
              
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowVendorModal(false)} className="bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-[10px] font-black uppercase tracking-widest px-6 py-2.5 rounded-sm transition-colors shadow-sm">Cancel</button>
                <button type="submit" form="vendor-form" disabled={!vendorForm.displayName.trim()} className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:text-slate-500 text-white text-[10px] font-black uppercase tracking-widest px-6 py-2.5 rounded-sm shadow-sm transition-colors">
                  {isEditingVendor ? 'Save Profile' : 'Establish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- DEAL DESK QUEUE: REVIEW & MINT OVERLAY --- */}
      {showDealModal && activeDeal && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="w-full max-w-6xl bg-slate-50 h-full border-l border-slate-300 shadow-2xl flex flex-col slide-in-from-right duration-300">
            
            <div className="bg-slate-900 p-6 border-b border-slate-700 flex justify-between items-center shrink-0">
              <div>
                <h2 className="text-xl font-black text-white uppercase tracking-tight">Deal Desk Authorization</h2>
                <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mt-1">
                  Master Provisioning & Revenue Override
                </p>
              </div>
              <button onClick={() => setShowDealModal(false)} className="text-slate-400 hover:text-white transition-colors">
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>

            {approvalSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-white">
                <div className="w-20 h-20 bg-emerald-50 border border-emerald-200 text-emerald-600 rounded-full flex items-center justify-center mb-6">
                  <i className="fa-solid fa-check text-4xl"></i>
                </div>
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-widest mb-2">Deal Authorized & Provisioned</h2>
                <p className="text-sm font-bold text-slate-500 mb-8 max-w-md">
                  The Master Agent has been minted and cleared from the queue. The secure FTL initialization link has been dispatched to {activeDeal.contactEmail || activeDeal.email}.
                </p>
                <div className="flex flex-col gap-4 w-full max-w-sm">
                  <button onClick={() => setShowDealModal(false)} className="bg-slate-900 hover:bg-slate-800 text-white py-4 px-8 rounded-sm text-[10px] font-black uppercase tracking-widest transition-colors shadow-sm">
                    Return to Queue
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 h-full">
                  
                  {/* LEFT COLUMN: Data Verification */}
                  <div className="space-y-6 border-r border-slate-200 pr-8">
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-200 pb-3 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]"><i className="fa-solid fa-lock"></i></span> Immutable Deal Package
                    </h3>
                    
                    <div className="bg-white border border-slate-200 p-5 rounded-sm shadow-sm space-y-4">
                      <div>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Corporate Entity</p>
                        <p className="text-sm font-black text-slate-900">{activeDeal.company_name || activeDeal.company}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">EIN / Tax ID</p>
                          <p className="text-sm font-mono text-slate-700 font-bold">{activeDeal.ein || 'Missing'}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Assigned Rep</p>
                          <p className="text-sm font-bold text-slate-700">{activeDeal.rep_name || 'System'}</p>
                        </div>
                      </div>
                      <div className="border-t border-slate-100 pt-4 mt-2">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Signatory Contact</p>
                        <p className="text-sm font-bold text-slate-900">{activeDeal.contact_name || activeDeal.contactName}</p>
                        <p className="text-xs font-medium text-slate-500 mt-1"><i className="fa-regular fa-envelope"></i> {activeDeal.contactEmail || activeDeal.email}</p>
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 p-5 rounded-sm shadow-sm">
                      <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Vaulted Documents</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-sm">
                          <div className="flex items-center gap-3">
                            <i className="fa-solid fa-file-contract text-lg text-slate-400"></i>
                            <div>
                              <p className="text-xs font-bold text-slate-900">Signed NDA</p>
                              {activeDeal.documents?.nda ? (
                                <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mt-0.5">Uploaded</p>
                              ) : (
                                <p className="text-[8px] font-black text-amber-500 uppercase tracking-widest mt-0.5">Missing</p>
                              )}
                            </div>
                          </div>
                          {activeDeal.documents?.nda && (
                            <button onClick={() => window.open(activeDeal.documents.nda, '_blank')} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm">
                              View PDF
                            </button>
                          )}
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-sm">
                          <div className="flex items-center gap-3">
                            <i className="fa-solid fa-building-columns text-lg text-slate-400"></i>
                            <div>
                              <p className="text-xs font-bold text-slate-900">EIN Verification</p>
                              {activeDeal.documents?.ein ? (
                                <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mt-0.5">Uploaded</p>
                              ) : (
                                <p className="text-[8px] font-black text-amber-500 uppercase tracking-widest mt-0.5">Missing</p>
                              )}
                            </div>
                          </div>
                          {activeDeal.documents?.ein && (
                            <button onClick={() => window.open(activeDeal.documents.ein, '_blank')} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm">
                              View PDF
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT COLUMN: Founder Adjustments */}
                  <div className="space-y-6">
                    <form id="deal-approval-form" onSubmit={submitDealApproval}>
                      <h3 className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest border-b border-emerald-100 pb-3 mb-5 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 font-bold text-[9px]"><i className="fa-solid fa-sliders"></i></span> Founder Overrides & Margins
                      </h3>

                      <div className="bg-slate-900 text-white p-5 shadow-inner mb-6 flex items-center justify-between gap-4 rounded-sm">
                        <div>
                          <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-1">Global Revenue Split</p>
                          <p className="text-xs text-slate-400 font-medium">SaaS MRR routed to the Master Agent's Stripe Connect.</p>
                        </div>
                        <div className="relative w-28 shrink-0">
                          <input type="number" step="0.1" value={dealApprovalForm.overrideCutPercent} onChange={(e) => setDealApprovalForm({...dealApprovalForm, overrideCutPercent: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white px-3 py-2.5 text-base font-black focus:outline-none focus:border-emerald-500 text-center rounded-sm" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">%</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Network Implementation Fee</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                            <input type="number" value={dealApprovalForm.setupFee} onChange={(e) => setDealApprovalForm({...dealApprovalForm, setupFee: e.target.value})} className="w-full bg-slate-50 border border-slate-300 pl-8 pr-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Network Subscription (MRR)</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                            <input type="number" value={dealApprovalForm.mrr} onChange={(e) => setDealApprovalForm({...dealApprovalForm, mrr: e.target.value})} className="w-full bg-slate-50 border border-slate-300 pl-8 pr-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
                          </div>
                        </div>
                      </div>

                      <div className="bg-white border border-slate-200 p-5 rounded-sm shadow-sm mb-6">
                        <div className="grid grid-cols-12 gap-y-4 items-center">
                          <div className="col-span-8 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Line Item</div>
                          <div className="col-span-4 text-[10px] font-bold text-emerald-600 uppercase tracking-widest border-b border-slate-100 pb-2 text-center">Approved Rate</div>

                          <div className="col-span-8 text-xs font-bold text-slate-700">Monthly Door Fee (MRR)</div>
                          <div className="col-span-4 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>
                            <input type="number" value={dealApprovalForm.negotiated_door_fee} onChange={(e) => setDealApprovalForm({...dealApprovalForm, negotiated_door_fee: e.target.value})} className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 pl-6 pr-2 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm" />
                          </div>

                          <div className="col-span-8 text-xs font-bold text-slate-700">CheckHQ Location Fee</div>
                          <div className="col-span-4 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>
                            <input type="number" value={dealApprovalForm.negotiated_payroll_base} onChange={(e) => setDealApprovalForm({...dealApprovalForm, negotiated_payroll_base: e.target.value})} className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 pl-6 pr-2 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm" />
                          </div>

                          <div className="col-span-8 text-xs font-bold text-slate-700">Telecom Activation Bounty</div>
                          <div className="col-span-4 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>
                            <input type="number" step="0.50" value={dealApprovalForm.negotiated_activation_bounty} onChange={(e) => setDealApprovalForm({...dealApprovalForm, negotiated_activation_bounty: e.target.value})} className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 pl-6 pr-2 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm" />
                          </div>

                          {dynamicMargins.map(margin => (
                            <React.Fragment key={margin.systemKey}>
                              <div className="col-span-8 text-xs font-bold text-slate-700">{margin.displayName}</div>
                              <div className="col-span-4 relative">
                                {!margin.isPercent && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>}
                                <input 
                                  type="number" step={margin.isPercent ? "0.1" : "0.001"} 
                                  value={dealApprovalForm.dynamicOverrides[margin.systemKey] ?? margin.retail} 
                                  onChange={(e) => handleApprovalDynamicOverrideChange(margin.systemKey, e.target.value)} 
                                  className={`w-full bg-emerald-50 border border-emerald-200 text-emerald-700 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm ${margin.isPercent ? 'pr-6 pl-2' : 'pl-6 pr-2'}`}
                                />
                                {margin.isPercent && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">%</span>}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 shadow-sm mb-6">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" checked={dealApprovalForm.adminOverride} onChange={(e) => setDealApprovalForm({...dealApprovalForm, adminOverride: e.target.checked})} className="w-5 h-5 text-amber-600 focus:ring-amber-500 border-amber-400 rounded-sm" />
                          <span className="text-xs font-black text-amber-900 uppercase tracking-widest">Founder Override (Bypass Stripe Requirement)</span>
                        </label>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {!approvalSuccess && (
              <div className="p-6 border-t border-slate-300 bg-slate-100 flex items-center justify-between gap-4 shrink-0">
                <button onClick={() => setShowDealModal(false)} type="button" className="px-8 py-3 text-[10px] font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 uppercase tracking-widest transition-colors shadow-sm rounded-sm">
                  Cancel
                </button>
                <button disabled={isApproving} type="submit" form="deal-approval-form" className={`px-8 py-3 text-[10px] font-bold text-white uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50 rounded-sm ${dealApprovalForm.adminOverride ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-500'}`}>
                  {isApproving ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i> Authorizing...</> : (
                    dealApprovalForm.adminOverride ? <><i className="fa-solid fa-bolt mr-2"></i> Force Provision</> : <><i className="fa-solid fa-paper-plane mr-2"></i> Authorize & Dispatch FTL</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- FULL MASTER AGENT INTAKE ENGINE OVERLAY (MANUAL TAB) --- */}
      {showAgentIntake && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
          <div className="w-full max-w-3xl bg-slate-50 h-full border-l border-slate-300 shadow-2xl flex flex-col slide-in-from-right duration-300">
            
            <div className="bg-slate-900 p-6 border-b border-slate-700 flex justify-between items-center shrink-0">
              <div>
                <h2 className="text-xl font-black text-white uppercase tracking-tight">Master Agent Intake</h2>
                <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mt-1">Enterprise Contract & Provisioning</p>
              </div>
              <button onClick={() => setShowAgentIntake(false)} className="text-slate-400 hover:text-white transition-colors">
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>

            {intakeSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-white">
                <div className="w-20 h-20 bg-emerald-50 border border-emerald-200 text-emerald-600 rounded-full flex items-center justify-center mb-6">
                  <i className="fa-solid fa-check text-4xl"></i>
                </div>
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-widest mb-2">Master Agent Provisioned</h2>
                <p className="text-sm font-bold text-slate-500 mb-8 max-w-md">
                  The Master Agent environment has been compiled. A secure FTL initialization link has been dispatched to {agentForm.email}.
                </p>
                <div className="flex flex-col gap-4 w-full max-w-sm">
                  <button onClick={() => setShowAgentIntake(false)} className="bg-slate-900 hover:bg-slate-800 text-white py-4 px-8 rounded-sm text-[10px] font-black uppercase tracking-widest transition-colors shadow-sm">
                    Return to Workspace
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar">
                <form id="master-intake-form" onSubmit={submitMasterAgent}>
                  
                  {/* SECTION 1: PROVISIONING PIPELINE */}
                  <div className="mb-10">
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-200 pb-3 mb-5">
                      1. Provisioning Pipeline Setup
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <label className={`cursor-pointer border-2 p-4 transition-all rounded-sm ${agentForm.provisioningMethod === 'vault' ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                        <input type="radio" name="provisioningMethod" value="vault" checked={agentForm.provisioningMethod === 'vault'} onChange={handleFormChange} className="hidden" />
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-800">Direct Manual Vault</span>
                          <span className="text-[8px] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-sm">IN-HOUSE</span>
                        </div>
                        <p className="text-xs text-slate-500 font-medium">Bypass API. Securely invite Master Agent to CRM vault to upload legal & compliance docs.</p>
                      </label>

                      <label className={`cursor-pointer border-2 p-4 transition-all rounded-sm ${agentForm.provisioningMethod === 'checkhq' ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                        <input type="radio" name="provisioningMethod" value="checkhq" checked={agentForm.provisioningMethod === 'checkhq'} onChange={handleFormChange} className="hidden" />
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-800">CheckHQ Invite</span>
                          <span className="text-[8px] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-sm">API FLOW</span>
                        </div>
                        <p className="text-xs text-slate-500 font-medium">Provision corporate ID via CheckHQ API and dispatch encrypted self-onboarding link.</p>
                      </label>
                    </div>
                  </div>

                  {/* SECTION 2: ENTITY */}
                  <div className="bg-white border border-slate-200 p-6 shadow-sm mb-10 rounded-sm">
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">2</span> Corporate Legal Entity
                    </h3>
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Legal Corporate Name <span className="text-red-500">*</span></label>
                        <input required type="text" name="companyName" value={agentForm.companyName} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="e.g., Summit Retail LLC" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">DBA (Optional)</label>
                        <input type="text" name="dba" value={agentForm.dba} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="e.g., Summit Dealers" />
                      </div>
                      
                      <div className="col-span-2">
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Corporate EIN / Tax ID <span className="text-red-500">*</span></label>
                        <input required type="text" name="ein" value={agentForm.ein} onChange={handleFormChange} maxLength="10" className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none tracking-widest rounded-sm" placeholder="XX-XXXXXXX" />
                      </div>

                      <div className="col-span-2 grid grid-cols-12 gap-4 border-t border-slate-100 pt-5 mt-2">
                        <div className="col-span-12 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-[-5px]">Corporate Headquarters</div>
                        <div className="col-span-12">
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Street Address <span className="text-red-500">*</span></label>
                          <input required type="text" name="billingStreet" value={agentForm.billingStreet} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="123 Corporate Blvd, Suite 100" />
                        </div>
                        <div className="col-span-6 md:col-span-5">
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">City <span className="text-red-500">*</span></label>
                          <input required type="text" name="billingCity" value={agentForm.billingCity} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="Seattle" />
                        </div>
                        <div className="col-span-3 md:col-span-4">
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">State <span className="text-red-500">*</span></label>
                          <select required name="billingState" value={agentForm.billingState} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-2 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm">
                            {US_STATES.map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3 md:col-span-3">
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">ZIP <span className="text-red-500">*</span></label>
                          <input required type="text" name="billingZip" value={agentForm.billingZip} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="98101" />
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* SECTION 3: STAKEHOLDER & OPERATIONAL CONTACTS */}
                  <div className="bg-white border border-slate-200 p-6 shadow-sm mb-10 rounded-sm">
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">3</span> Executive Stakeholder & Contacts
                    </h3>
                    
                    {/* Executive */}
                    <div className="grid grid-cols-2 gap-5 mb-6">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Executive Name <span className="text-red-500">*</span></label>
                        <input required type="text" name="contactName" value={agentForm.contactName} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="Jane Doe" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Direct Phone <span className="text-red-500">*</span></label>
                        <input required type="tel" name="phone" value={agentForm.phone} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="(555) 123-4567" />
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Corporate Email <span className="text-red-500">*</span></label>
                        <input required type="email" name="email" value={agentForm.email} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="jane.doe@summit.com" />
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Stripe Connect ID (Routing)</label>
                        <input type="text" name="stripeAccountId" value={agentForm.stripeAccountId} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-mono text-slate-600 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="acct_1Ou..." />
                      </div>
                    </div>

                    {/* Operational Contacts */}
                    <div className="border-t border-slate-100 pt-6">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Operational Contacts (Optional)</p>
                      <div className="grid grid-cols-2 gap-5">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">A/P Billing Contact Name</label>
                          <input type="text" name="billingContactName" value={agentForm.billingContactName} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="Accounting Dept" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">A/P Billing Email</label>
                          <input type="email" name="billingContactEmail" value={agentForm.billingContactEmail} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="ap@summit.com" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Technical/IT Contact Name</label>
                          <input type="text" name="techContactName" value={agentForm.techContactName} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="IT Ops" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Technical/IT Email</label>
                          <input type="email" name="techContactEmail" value={agentForm.techContactEmail} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="support@summit.com" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 4: MARGIN NEGOTIATION */}
                  <div className="bg-white border border-slate-200 p-6 shadow-sm rounded-sm mb-10">
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">4</span> Rate Overrides & Splits
                    </h3>
                    
                    {/* Revenue Split */}
                    <div className="bg-slate-900 text-white p-5 shadow-inner mb-6 flex flex-col md:flex-row items-center justify-between gap-4 rounded-sm">
                      <div>
                        <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-1">Global Revenue Split</p>
                        <p className="text-xs text-slate-400 font-medium">Percentage of total SaaS MRR routed to the Master Agent's Stripe Connect account.</p>
                      </div>
                      <div className="relative w-32 shrink-0">
                        <input type="number" name="overrideCutPercent" step="0.1" value={agentForm.overrideCutPercent} onChange={handleFormChange} className="w-full bg-slate-800 border border-slate-600 text-white px-4 py-3 text-lg font-black focus:outline-none focus:border-emerald-500 text-center rounded-sm" />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">%</span>
                      </div>
                    </div>

                    {/* Negotiation Grid */}
                    <div className="grid grid-cols-12 gap-y-4 items-center">
                      <div className="col-span-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Line Item</div>
                      <div className="col-span-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 text-center">Global Rate</div>
                      <div className="col-span-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 text-center text-emerald-600">Negotiated</div>

                      <div className="col-span-6 text-xs font-bold text-slate-700">Monthly Door Fee (MRR)</div>
                      <div className="col-span-3 text-center text-xs text-slate-400 font-mono">${margins.monthly_door_fee?.toFixed(2)}</div>
                      <div className="col-span-3 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>
                        <input type="number" name="negotiated_door_fee" value={agentForm.negotiated_door_fee} onChange={handleFormChange} className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 pl-6 pr-2 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm" />
                      </div>

                      <div className="col-span-6 text-xs font-bold text-slate-700">CheckHQ Location Fee</div>
                      <div className="col-span-3 text-center text-xs text-slate-400 font-mono">${margins.payroll_base_margin?.toFixed(2)}</div>
                      <div className="col-span-3 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>
                        <input type="number" name="negotiated_payroll_base" value={agentForm.negotiated_payroll_base} onChange={handleFormChange} className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 pl-6 pr-2 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm" />
                      </div>

                      <div className="col-span-6 text-xs font-bold text-slate-700">CheckHQ Employee Fee</div>
                      <div className="col-span-3 text-center text-xs text-slate-400 font-mono">${margins.payroll_employee_margin?.toFixed(2)}</div>
                      <div className="col-span-3 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>
                        <input type="number" name="negotiated_payroll_emp" value={agentForm.negotiated_payroll_emp} onChange={handleFormChange} className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 pl-6 pr-2 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm" />
                      </div>

                      <div className="col-span-6 text-xs font-bold text-slate-700">Telecom Activation Bounty</div>
                      <div className="col-span-3 text-center text-xs text-slate-400 font-mono">${margins.activation_bounty?.toFixed(2)}</div>
                      <div className="col-span-3 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>
                        <input type="number" step="0.50" name="negotiated_activation_bounty" value={agentForm.negotiated_activation_bounty} onChange={handleFormChange} className="w-full bg-emerald-50 border border-emerald-200 text-emerald-700 pl-6 pr-2 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm" />
                      </div>

                      {dynamicMargins.map(margin => (
                        <React.Fragment key={margin.systemKey}>
                          <div className="col-span-6 text-xs font-bold text-slate-700">{margin.displayName}</div>
                          <div className="col-span-3 text-center text-xs text-slate-400 font-mono">
                            {margin.isPercent ? `${margin.retail}%` : `$${Number(margin.retail).toFixed(3)}`}
                          </div>
                          <div className="col-span-3 relative">
                            {!margin.isPercent && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">$</span>}
                            <input 
                              type="number" 
                              step={margin.isPercent ? "0.1" : "0.001"} 
                              value={agentForm.dynamicOverrides[margin.systemKey] ?? margin.retail} 
                              onChange={(e) => handleDynamicOverrideChange(margin.systemKey, e.target.value)} 
                              className={`w-full bg-emerald-50 border border-emerald-200 text-emerald-700 py-1.5 text-center text-sm font-black focus:outline-none rounded-sm ${margin.isPercent ? 'pr-6 pl-2' : 'pl-6 pr-2'}`}
                            />
                            {margin.isPercent && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black text-xs">%</span>}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  {/* SECTION 5: ENTERPRISE BILLING & PROVISIONING */}
                  <div className="bg-white border border-slate-200 p-6 shadow-sm rounded-sm mb-10">
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">5</span> Network Franchise & Billing
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Network Implementation Fee (One-Time)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                          <input type="number" name="setupFee" value={agentForm.setupFee} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 pl-8 pr-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Master Agent Subscription (MRR)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                          <input type="number" name="mrr" value={agentForm.mrr} onChange={handleFormChange} className="w-full bg-slate-50 border border-slate-300 pl-8 pr-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 shadow-sm">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" name="adminOverride" checked={agentForm.adminOverride} onChange={handleFormChange} className="w-5 h-5 text-amber-600 focus:ring-amber-500 border-amber-400 rounded-sm" />
                        <span className="text-xs font-black text-amber-900 uppercase tracking-widest">Founder Rights Override (Manual Provisioning - Bypass Stripe Requirement)</span>
                      </label>
                    </div>
                  </div>

                </form>
              </div>
            )}

            <div className="p-6 border-t border-slate-300 bg-slate-100 flex items-center justify-between gap-4 shrink-0">
              <button onClick={() => setShowAgentIntake(false)} type="button" className="px-8 py-3 text-[10px] font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 uppercase tracking-widest transition-colors shadow-sm rounded-sm">
                {intakeSuccess ? 'Close Workspace' : 'Cancel'}
              </button>
              
              {!intakeSuccess && (
                <button disabled={isSubmitting} type="submit" form="master-intake-form" className={`px-8 py-3 text-[10px] font-bold text-white uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50 rounded-sm ${agentForm.adminOverride ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-900 hover:bg-slate-800'}`}>
                  {isSubmitting ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i> Provisioning...</> : (
                    agentForm.adminOverride ? <><i className="fa-solid fa-bolt mr-2"></i> Force Provision</> : <><i className="fa-solid fa-paper-plane mr-2"></i> Provision & Dispatch FTL</>
                  )}
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* --- EEKOPAY FULLSCREEN DASHBOARD OVERLAY --- */}
      {showVault && <EekoPayDashboard onClose={() => setShowVault(false)} />}

    </div>
  );
}